/**
 * Panneau repliable : un en-tête cliquable (titre + résumé facultatif) qui
 * affiche / masque son contenu. Replié par défaut ; le contenu n'est monté
 * qu'une fois ouvert.
 */

import { useId, useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@jeraisonne/ui';

export function CollapsiblePanel({
  title,
  summary,
  defaultOpen = false,
  children,
  className,
}: {
  title: string;
  /** Une ligne sous le titre quand le panneau est replié (ex. état actuel). */
  summary?: ReactNode;
  defaultOpen?: boolean;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const contentId = useId();
  return (
    <div className={cn('border-ink-100 border-t pt-2', className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[2.75rem] w-full items-center justify-between gap-3 py-2 text-left active:scale-[0.99]"
      >
        <span className="min-w-0">
          <span className="text-ink-400 block text-xs font-semibold uppercase tracking-wider">
            {title}
          </span>
          {summary && !open && <span className="text-ink-500 mt-0.5 block text-xs">{summary}</span>}
        </span>
        <ChevronDown
          aria-hidden
          className={cn(
            'text-ink-400 h-5 w-5 shrink-0 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>
      {open && (
        <div id={contentId} className="space-y-5 pb-2 pt-2">
          {children}
        </div>
      )}
    </div>
  );
}
